import React, { useState, useRef, useEffect } from "react";
import Slider from "@mui/material/Slider";
import FilterListIcon from "@mui/icons-material/FilterList";

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom;

      const dropdownHeight = 220;

      setOpenUpward(spaceBelow < dropdownHeight);
    }

    setOpen(!open);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <div
        onClick={handleToggle}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 cursor-pointer flex items-center justify-between min-h-[38px]"
      >
        <span
          className={`truncate ${
            selected.length === 0
              ? "text-gray-400"
              : "text-gray-700"
          }`}
        >
          {selected.length === 0
            ? placeholder
            : selected.join(", ")}
        </span>

        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-1 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`
            absolute left-0 w-full bg-white border border-gray-200 rounded-xl shadow-lg
            z-[9999] max-h-48 overflow-y-auto overscroll-contain
            ${
              openUpward
                ? "bottom-full mb-1"
                : "top-full mt-1"
            }
          `}
          onWheel={(e) => e.stopPropagation()}
        >
          {options.map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm text-gray-700"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => onChange(opt)}
                className="accent-emerald-600 w-4 h-4 rounded"
              />

              <span>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WellnessFilterSidebar({
  priceRange,
  onPriceChange,
  maxPrice = 50000,
  selectedLocations,
  onLocationChange,
  selectedCategories,
  onCategoryChange,
  selectedDuration,
  onDurationChange,
  onClearAll,
  locationOptions = [],
  categoryOptions = [],
  vertical = false,
}) {
  const durations = [
    { key: "1-3", label: "1 to 3 days" },
    { key: "4-7", label: "4 to 7 days" },
    { key: "8-10", label: "8 to 10 days" },
    { key: "10+", label: "10+ days" },
  ];

  const durationOptions = durations.map(d => d.key);
  const durationLabel = (key) => durations.find(d => d.key === key)?.label || key;

  return (
    <div className={`bg-white ${vertical ? "" : "border rounded-2xl shadow-sm p-4 sticky top-20 z-30"}`}>

      {/* Header — hidden in vertical/drawer mode since drawer has its own */}
      {!vertical && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FilterListIcon className="text-emerald-600" />
            <span className="font-semibold text-gray-800">Filters</span>
          </div>
          <button onClick={onClearAll} className="text-sm text-emerald-600 hover:underline">
            Clear all
          </button>
        </div>
      )}

      {/* Filter groups */}
<div
  className={
    vertical
      ? "flex flex-col gap-3"
      : "flex gap-4 pb-2 overflow-visible"
  }
>
      
{/* Price */}
<div className={`bg-gray-50 rounded-xl p-3 ${vertical ? "w-full" : "min-w-[240px]"}`}>
  <div className="mb-2 text-sm font-medium text-gray-800">Price (₹)</div>
  <div className="flex items-center gap-2">
    <input
      type="number"
      min={0}
      value={priceRange[0] === 0 ? '' : priceRange[0]}
      onChange={(e) => {
        const val = e.target.value === '' ? 0 : Number(e.target.value);
        onPriceChange([val, priceRange[1]]);
      }}
      placeholder="Min"
      className="w-full min-h-[38px] border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
    />
    <span className="text-gray-400 flex-shrink-0">-</span>
    <input
      type="number"
      min={priceRange[0]}
      value={priceRange[1] === 5000000 ? '' : priceRange[1]}
      onChange={(e) => {
        const val = e.target.value === '' ? 5000000 : Number(e.target.value);
        onPriceChange([priceRange[0], val]);
      }}
      placeholder="Max"
      className="w-full min-h-[38px] border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
    />
  </div>
</div>

        {/* Location */}
        <div className={`bg-gray-50 rounded-xl p-3 ${vertical ? "w-full" : "min-w-[220px]"}`}>
          <div className="mb-2 text-sm font-medium text-gray-800">Location</div>
          <MultiSelectDropdown
            options={locationOptions}
            selected={selectedLocations}
            onChange={onLocationChange}
            placeholder="Select location"
          />
        </div>

        {/* Category */}
        <div className={`bg-gray-50 rounded-xl p-3 ${vertical ? "w-full" : "min-w-[220px]"}`}>
          <div className="mb-2 text-sm font-medium text-gray-800">Category</div>
          <MultiSelectDropdown
            options={categoryOptions}
            selected={selectedCategories}
            onChange={onCategoryChange}
            placeholder="Select category"
          />
        </div>

        {/* Duration */}
        <div className={`bg-gray-50 rounded-xl p-3 ${vertical ? "w-full" : "min-w-[220px]"}`}>
          <div className="mb-2 text-sm font-medium text-gray-800">Duration</div>
          <MultiSelectDropdown
            options={durationOptions}
            selected={selectedDuration}
            onChange={onDurationChange}
            placeholder="Select duration"
          />
          {selectedDuration.length > 0 && (
            <div className="mt-2 text-xs text-gray-500 truncate">
              {selectedDuration.map(durationLabel).join(", ")}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}